import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Chip, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';
import useAuth from '../../../hooks/useAuth';
import DataTable from '../../../components/design/DataTable';
import LoadingState from '../../../components/design/LoadingState';
import EmptyState from '../../../components/design/EmptyState';
import externalApiService from '../../../services/externalApiService';
import unifiedSocket from '../../../services/unifiedSocket';

function statusChip(value) {
  const v = String(value || '').toLowerCase();
  if (v === 'waiting' || v === 'delayed') return <Chip size="small" label={value} color="warning" />;
  if (v === 'active') return <Chip size="small" label={value} color="info" />;
  if (v === 'failed') return <Chip size="small" label={value} color="error" />;
  if (v === 'completed') return <Chip size="small" label={value} color="success" />;
  return <Chip size="small" label={value || '—'} />;
}

export default function ExternalApiQueuePage() {
  const { user: authUser } = useAuth();
  const currentUser = authUser?.user || authUser || null;
  const token = authUser?.token || null;
  const socketUserId = currentUser?.id || null;
  const companyId = currentUser?.companyId || 1;

  const [loading, setLoading] = useState(false);
  const [queueStats, setQueueStats] = useState(null);
  const [integrations, setIntegrations] = useState([]);

  const [filters, setFilters] = useState({ integrationId: '', requestUuid: '' });
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [total, setTotal] = useState(0);
  const [rows, setRows] = useState([]);

  const loadIntegrations = useCallback(async () => {
    const resp = await externalApiService.listIntegrations().catch(() => null);
    const data = resp?.data?.data || resp?.data || [];
    const list = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
    setIntegrations(list);
  }, []);

  const loadQueueStats = useCallback(async () => {
    const resp = await externalApiService.getQueueStats().catch(() => null);
    const payload = resp?.data?.data || resp?.data || null;
    setQueueStats(payload);
  }, []);

  const loadDeadletters = useCallback(async (nextPage = 1) => {
    setLoading(true);
    try {
      const resp = await externalApiService.listDeadletters({
        page: nextPage,
        limit,
        integrationId: filters.integrationId || undefined,
        requestUuid: filters.requestUuid || undefined
      });
      const payload = resp?.data?.data || resp?.data;
      const data = payload?.data || payload;
      const outRows = data?.rows || [];
      const outCount = data?.count || 0;
      setRows(outRows);
      setTotal(outCount);
      setPage(nextPage);
    } finally {
      setLoading(false);
    }
  }, [filters.integrationId, filters.requestUuid, limit]);

  const refreshAll = useCallback(async () => {
    await Promise.all([loadIntegrations(), loadQueueStats(), loadDeadletters(1)]);
  }, [loadIntegrations, loadQueueStats, loadDeadletters]);

  useEffect(() => {
    refreshAll().catch(() => {});
  }, [refreshAll]);

  useEffect(() => {
    if (!token || !socketUserId) return;
    unifiedSocket.connect({ id: socketUserId, companyId, token });
    const socket = unifiedSocket.getSocket?.();
    if (!socket) return;

    const onQueueEvent = () => {
      loadQueueStats().catch(() => {});
      loadDeadletters(page).catch(() => {});
    };
    socket.on('externalQueue.event', onQueueEvent);
    return () => {
      try { socket.off('externalQueue.event', onQueueEvent); } catch {}
    };
  }, [token, socketUserId, companyId, loadQueueStats, loadDeadletters, page]);

  const columnsQueue = useMemo(() => ([
    { key: 'name', label: 'Fila' },
    { key: 'value', label: 'Qtd', align: 'right' }
  ]), []);

  const queueRows = useMemo(() => {
    const counts = queueStats?.counts || {};
    const keys = ['waiting', 'active', 'delayed', 'failed', 'completed', 'paused'];
    return keys.map(k => ({ name: k, value: Number(counts?.[k] || 0) }));
  }, [queueStats]);

  const columnsDlq = useMemo(() => ([
    { key: 'id', label: 'ID', align: 'right' },
    { key: 'createdAt', label: 'Criado em', render: (v) => (v ? new Date(v).toLocaleString() : '—') },
    { key: 'jobName', label: 'Job' },
    { key: 'jobId', label: 'Job ID' },
    { key: 'requestUuid', label: 'Request' },
    { key: 'failedReason', label: 'Motivo' },
    { key: 'actions', label: '', align: 'right', render: (_, row) => (
      <Button
        size="small"
        variant="outlined"
        disabled={!row?.externalMessageRequestId}
        onClick={async () => {
          setLoading(true);
          try {
            await externalApiService.reprocessDeadletter(row.id);
            await loadQueueStats();
            await loadDeadletters(page);
          } finally {
            setLoading(false);
          }
        }}
      >
        Reprocessar
      </Button>
    ) }
  ]), [loadDeadletters, loadQueueStats, page]);

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" gutterBottom>
        Monitoramento ERP (Fila/DLQ)
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }} justifyContent="space-between">
          <Box>
            <Typography variant="subtitle1">Fila BullMQ: {queueStats?.queue || 'system'}</Typography>
            <Typography variant="body2" color="text.secondary">
              Worker concurrency: {queueStats?.workerConcurrency ?? '—'}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button variant="outlined" onClick={() => refreshAll().catch(() => {})}>Atualizar</Button>
          </Stack>
        </Stack>
        <Box sx={{ mt: 2 }}>
          <DataTable
            columns={columnsQueue}
            rows={queueRows}
            getKey={(r) => r.name}
          />
        </Box>
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" gutterBottom>DLQ</Typography>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
          <TextField
            select
            size="small"
            label="Integração"
            value={filters.integrationId}
            onChange={(e) => setFilters(prev => ({ ...prev, integrationId: e.target.value }))}
            sx={{ minWidth: 220 }}
          >
            <MenuItem value="">Todas</MenuItem>
            {integrations.map(i => (
              <MenuItem key={i.id} value={String(i.id)}>{i.name}</MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            label="request_uuid"
            value={filters.requestUuid}
            onChange={(e) => setFilters(prev => ({ ...prev, requestUuid: e.target.value }))}
            fullWidth
          />
          <Button variant="contained" onClick={() => loadDeadletters(1).catch(() => {})}>Filtrar</Button>
        </Stack>

        {loading && <LoadingState />}
        {!loading && rows.length === 0 && <EmptyState title="Sem DLQ" description="Nenhum item em DLQ para os filtros selecionados." />}
        {!loading && rows.length > 0 && (
          <>
            <DataTable columns={columnsDlq} rows={rows} getKey={(r) => r.id} />
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end" sx={{ mt: 1 }}>
              <Chip size="small" label={`Total: ${total}`} />
              <Button size="small" disabled={page <= 1} onClick={() => loadDeadletters(page - 1).catch(() => {})}>Anterior</Button>
              <Chip size="small" label={`${page}/${totalPages}`} />
              <Button size="small" disabled={page >= totalPages} onClick={() => loadDeadletters(page + 1).catch(() => {})}>Próxima</Button>
            </Stack>
          </>
        )}
      </Paper>
    </Box>
  );
}
